/**
 * Mirrors Angular PR #1170 (CS2NEW-1496) resident/reimbursement decoupling helpers.
 *
 * ID helpers return `undefined` (not `null`) so they are assignable to optional
 * React hook params typed as `string | number | undefined`.
 */

import type { ResidentDetailTab } from '../types/domain'
import { isReimbursementClaimType } from './residentHeaderStatus'

export type DefaultApiPage = 'resident' | 'default'
export type ResidentDetailPageType = 'resident-detail' | 'default-detail'

/** Route param on `/defaults/default-detail/:applicantId/:userId/:reimbursementId`. */
export const REIMBURSEMENT_ID_ROUTE_PARAM = 'reimbursementId'

export function appendReimbursementIdForDefaultPage(
  payload: Record<string, unknown>,
  reimbursementId?: string | number,
): void {
  if (reimbursementId === undefined || reimbursementId === null || String(reimbursementId).trim() === '') {
    return
  }
  if (payload.page !== 'default') return
  payload.reimbursement_id = String(reimbursementId)
}

export function appendReimbursementIdToFormData(
  formData: FormData,
  reimbursementId?: string | number,
): void {
  if (reimbursementId === undefined || reimbursementId === null || String(reimbursementId).trim() === '') {
    return
  }
  if (formData.get('page') !== 'default') return
  if (!formData.has('reimbursement_id')) {
    formData.append('reimbursement_id', String(reimbursementId))
  }
}

/** Reimbursement module chrome (header REIMBURSEMENT card + tab) — default-detail route only. */
export function shouldShowReimbursementDetailChrome(
  pageType: ResidentDetailPageType,
  reimbursementType?: string | null,
): boolean {
  return pageType === 'default-detail' && isReimbursementClaimType(reimbursementType)
}

/** POST `page` from active tab: Reimbursement + Calculation use `default` (not route URL). */
export function apiPageForDetailTab(selectedTab: ResidentDetailTab): DefaultApiPage {
  return selectedTab === 'default' || selectedTab === 'calculation' ? 'default' : 'resident'
}

type IdLike = string | number | null | undefined

function firstId(...candidates: IdLike[]): string | number | undefined {
  for (const value of candidates) {
    if (value === null || value === undefined) continue
    if (typeof value === 'string' && value.trim() === '') continue
    return value
  }
  return undefined
}

/** Claim PK after decoupling: reimbursement_id, often still aliased as fk_resident_default_id. */
export function getReimbursementId(
  detail: Record<string, unknown> | null | undefined,
): string | number | undefined {
  if (!detail) return undefined
  const rd = detail.resident_default as Record<string, unknown> | undefined
  const reimbursementRecord = detail.reimbursement_record as Record<string, unknown> | null | undefined
  const reimbursementDetail = (detail.reimbursement_detail || detail.reimbursement) as
    | Record<string, unknown>
    | undefined

  if (!rd && !reimbursementRecord && !reimbursementDetail) return undefined

  return firstId(
    rd?.fk_reimbursement_id as IdLike,
    rd?.fk_resident_default_id as IdLike,
    rd?.reimbursement_id as IdLike,
    reimbursementRecord?.reimbursement_id as IdLike,
    reimbursementDetail?.fk_reimbursement_id as IdLike,
    reimbursementDetail?.reimbursement_id as IdLike,
  )
}

/** Resident lifecycle PK after decoupling. */
export function getResidentId(
  detail: Record<string, unknown> | null | undefined,
): string | number | undefined {
  if (!detail) return undefined
  const rd = detail.resident_default as Record<string, unknown> | undefined
  const resident = detail.resident as Record<string, unknown> | undefined
  return firstId(rd?.fk_resident_id as IdLike, resident?.resident_id as IdLike)
}

export type NotesFor = 'RESIDENT' | 'DEFAULT'

/**
 * Page-scoped notes key:
 * - resident-detail → notes_for=RESIDENT, fk_ref_id=resident_id
 * - default-detail → notes_for=DEFAULT, fk_ref_id=reimbursement_id
 */
export function resolveNotesRef(
  detail: Record<string, unknown> | null | undefined,
  pageType: ResidentDetailPageType = 'resident-detail',
): { refId?: string | number; notesFor: NotesFor } {
  if (pageType === 'resident-detail') {
    return { refId: getResidentId(detail), notesFor: 'RESIDENT' }
  }
  return { refId: getReimbursementId(detail), notesFor: 'DEFAULT' }
}

/**
 * GET/POST `page` flag: resident lifecycle vs reimbursement claim.
 * Prefer `selectedTab` (active tab); fall back to route + rental status for legacy callers.
 */
export function getDefaultApiPage(options: {
  selectedTab?: ResidentDetailTab | null
  pageType?: ResidentDetailPageType
  rentalStatus?: string | null
}): DefaultApiPage {
  if (options.selectedTab) {
    return apiPageForDetailTab(options.selectedTab)
  }
  const status = String(options.rentalStatus ?? '').trim()
  if (
    options.pageType === 'resident-detail' &&
    !['SKIPPED', 'EVICTION'].includes(status)
  ) {
    return 'resident'
  }
  return 'default'
}

/** ID for OLD_PAYMENT_STATEMENT upload: always the reimbursement id. */
export function resolveOldStatementUploadId(
  detail: Record<string, unknown> | null | undefined,
): string | number | undefined {
  return getReimbursementId(detail)
}

/**
 * File row PK after decoupling (Angular guidance-popup / document remove).
 * Prefer legacy `attachment_id`, then table-specific PKs.
 */
export function resolveAttachmentId(
  row: Record<string, unknown> | null | undefined,
): string | number | undefined {
  if (!row) return undefined
  return firstId(
    row.attachment_id as IdLike,
    row.resident_attachment_id as IdLike,
    row.reimbursement_attachment_id as IdLike,
    row.id as IdLike,
    row.file_id as IdLike,
  )
}

/**
 * Merge decoupled `resident` / `reimbursement_*` shapes into legacy `resident_default`
 * so existing React forms keep working.
 */
export function normalizeResidentDetail<T extends Record<string, unknown>>(
  response: T | null | undefined,
  pageType: ResidentDetailPageType = 'resident-detail',
): T | null | undefined {
  if (!response) return response

  const reimbursementDetail = {
    ...((response.reimbursement_detail || response.reimbursement || {}) as Record<string, unknown>),
  }
  const resident = { ...((response.resident || {}) as Record<string, unknown>) }
  const reimbursementRecord = (response.reimbursement_record || null) as Record<
    string,
    unknown
  > | null
  const legacy = {
    ...((response.resident_default || { ...reimbursementDetail }) as Record<string, unknown>),
  }

  if (pageType === 'resident-detail') {
    if (resident.rental_status) legacy.rental_status = resident.rental_status
    if (resident.rental_progress) legacy.rental_progress = resident.rental_progress
  } else if (
    reimbursementDetail.rental_status &&
    ['SKIPPED', 'EVICTION'].includes(String(reimbursementDetail.rental_status))
  ) {
    legacy.rental_status = reimbursementDetail.rental_status
    legacy.rental_progress = reimbursementDetail.rental_progress
  } else if (!legacy.rental_status || !['SKIPPED', 'EVICTION'].includes(String(legacy.rental_status))) {
    if (resident.rental_status) {
      legacy.rental_status = resident.rental_status
      legacy.rental_progress = resident.rental_progress
    }
  }

  ;(['lease_start_date', 'lease_end_date', 'lease_verified', 'lease_verified_date'] as const).forEach(
    (field) => {
      if (resident[field]) legacy[field] = resident[field]
    },
  )

  const claimId = firstId(
    legacy.fk_resident_default_id as IdLike,
    legacy.fk_reimbursement_id as IdLike,
    reimbursementDetail.fk_reimbursement_id as IdLike,
    reimbursementRecord?.reimbursement_id as IdLike,
  )
  if (claimId !== undefined) {
    legacy.fk_resident_default_id = claimId
    legacy.fk_reimbursement_id = claimId
    legacy.reimbursement_id = claimId
  }
  const residentId = firstId(legacy.fk_resident_id as IdLike, resident.resident_id as IdLike)
  if (residentId !== undefined) {
    legacy.fk_resident_id = residentId
  }

  if (resident.rental_status) {
    legacy.resident_rental_status = resident.rental_status
  }
  if (resident.rental_progress) {
    legacy.resident_rental_progress = resident.rental_progress
  }
  const reimbSource = reimbursementRecord ?? reimbursementDetail
  if (reimbSource.rental_status) {
    legacy.reimbursement_rental_status = reimbSource.rental_status
  }
  if (reimbSource.rental_progress) {
    legacy.reimbursement_rental_progress = reimbSource.rental_progress
  }
  if (reimbSource.reimbursement_type) {
    legacy.reimbursement_type = reimbSource.reimbursement_type
  }

  for (const field of ['core_section_status', 'unique_section_status', 'final_section_status'] as const) {
    const value = firstId(legacy[field] as IdLike, reimbursementDetail[field] as IdLike)
    if (value !== undefined) {
      legacy[field] = value
    }
  }

  const rejected = reimbursementDetail.reimbursement_rejected || legacy.reimbursement_rejected
  const uploaded = reimbursementDetail.reimbursement_uploaded || legacy.reimbursement_uploaded
  const received = reimbursementDetail.reimbursement_received || legacy.reimbursement_received
  if (rejected) legacy.resident_default_rejected = rejected
  if (uploaded) legacy.resident_default_uploaded = uploaded
  if (received) legacy.resident_default_received = received

  const next = { ...response } as T
  ;(next as Record<string, unknown>).resident_default = legacy
  if (response.reimbursement_detail) {
    ;(next as Record<string, unknown>).reimbursement_detail = reimbursementDetail
  }
  if (response.resident) {
    ;(next as Record<string, unknown>).resident = resident
  }
  if (response.reimbursement_record) {
    ;(next as Record<string, unknown>).reimbursement_record = reimbursementRecord
  }
  if (!response.resident_default_next_steps && response.reimbursement_next_steps) {
    ;(next as Record<string, unknown>).resident_default_next_steps = response.reimbursement_next_steps
  }

  return next
}
